import { Box, Button, CircularProgress, Icon } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import EmailProviderRow from "~/components/settings/EmailProviderRow";
import OutlookAdminApprovalNotice from "~/components/settings/OutlookAdminApprovalNotice";
import useOutlookMailboxConnect from "~/hooks/useOutlookMailboxConnect";
import M365ConnectDialog from "~fulfill/orders/components/M365ConnectDialog";

const OutlookMailboxConnectCard = () => {
	const { t } = useTranslation("common");
	const { connect, connecting, disconnect, isDisconnecting, isError, isLoading, status } = useOutlookMailboxConnect();
	const [reconnectDialogOpen, setReconnectDialogOpen] = useState(false);

	const connected = status?.connected ?? false;
	const grantsSendScope = status?.grantsSendScope ?? false;
	const mailboxAddress = status?.mailboxAddress;
	const draftsOnly = connected && !grantsSendScope;

	let statusMessage: string;
	if (!connected) {
		statusMessage = t("preferences.linkedEmail.outlookStatus.notConnected");
	} else if (draftsOnly) {
		statusMessage = mailboxAddress
			? t("preferences.linkedEmail.outlookStatus.draftsOnly", { mailboxAddress })
			: t("preferences.linkedEmail.outlookStatus.draftsOnlyNoAddress");
	} else {
		statusMessage = mailboxAddress
			? t("preferences.linkedEmail.outlookStatus.connected", { mailboxAddress })
			: t("preferences.linkedEmail.outlookStatus.connectedNoAddress");
	}
	if (isLoading) statusMessage = "Checking your mailbox connection...";
	else if (isError) statusMessage = "Could not check your mailbox connection.";

	const disconnectButton = (
		<Button
			color="error"
			disabled={isDisconnecting}
			onClick={disconnect}
			size="small"
			startIcon={isDisconnecting ? <CircularProgress color="inherit" size={16} /> : <Icon>link_off</Icon>}
			variant="outlined"
		>
			{isDisconnecting ? "Disconnecting..." : "Disconnect"}
		</Button>
	);

	const action = draftsOnly ? (
		<Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
			<Button
				onClick={() => setReconnectDialogOpen(true)}
				size="small"
				startIcon={<Icon>sync</Icon>}
				variant="contained"
			>
				{t("preferences.linkedEmail.outlookStatus.reconnect")}
			</Button>
			{disconnectButton}
		</Box>
	) : connected ? (
		disconnectButton
	) : (
		<Button
			disabled={connecting || isLoading}
			onClick={connect}
			size="small"
			startIcon={connecting ? <CircularProgress color="inherit" size={16} /> : <Icon>link</Icon>}
			variant="outlined"
		>
			{connecting ? "Connecting..." : "Connect mailbox"}
		</Button>
	);

	return (
		<>
			<EmailProviderRow
				action={action}
				icon="mail"
				statusColor={isError ? "error.main" : "text.secondary"}
				statusText={statusMessage}
				title="Microsoft 365 / Outlook"
			/>
			{!isLoading && !connected && <OutlookAdminApprovalNotice />}
			<M365ConnectDialog
				onClose={() => setReconnectDialogOpen(false)}
				open={reconnectDialogOpen}
				reason="send-permission"
			/>
		</>
	);
};

export default OutlookMailboxConnectCard;
